import React, { useEffect, useRef, useState } from 'react';
import { RowItem } from '../recs/engine';
import { UserProfile } from '../data/types';
import { posterUrlFor, genreGradientFor } from '../data/posters';
import './HeroCarousel.css';

const AUTOPLAY_MS = 6000;

interface Props {
  items: RowItem[];
  viewer: UserProfile;
  onSelect: (item: RowItem) => void;
}

export default function HeroCarousel({ items, viewer, onSelect }: Props) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    setIndex(0);
  }, [items]);

  useEffect(() => {
    if (paused || items.length <= 1) return;
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % items.length);
    }, AUTOPLAY_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [paused, items.length]);

  if (items.length === 0) return null;

  const item = items[index];
  const { title } = item;
  const isPremiumLocked = title.access_tier === 'Premium' && viewer.access_tier === 'Free';

  const goTo = (next: number) => setIndex((next + items.length) % items.length);

  return (
    <section
      className="hero"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="hero__layers">
        {items.map((it, i) => {
          const posterUrl = posterUrlFor(it.title.title_id, it.title.genre_primary);
          const gradient = genreGradientFor(it.title.genre_primary);
          return (
            <div
              key={it.title.title_id}
              className={`hero__layer ${i === index ? 'hero__layer--active' : ''}`}
              style={
                posterUrl
                  ? { backgroundImage: `linear-gradient(90deg, rgba(28,28,26,0.92) 20%, rgba(28,28,26,0.35) 65%, rgba(28,28,26,0.05) 100%), url(${posterUrl})` }
                  : { backgroundImage: `linear-gradient(90deg, rgba(28,28,26,0.55) 0%, rgba(28,28,26,0.2) 100%), ${gradient}` }
              }
            />
          );
        })}
      </div>

      <button className="hero__arrow hero__arrow--prev" aria-label="Previous" onClick={() => goTo(index - 1)}>
        ‹
      </button>
      <button className="hero__arrow hero__arrow--next" aria-label="Next" onClick={() => goTo(index + 1)}>
        ›
      </button>

      <div className="hero__content" key={title.title_id}>
        <p className="hero__eyebrow">{item.reason ?? `Trending · ${title.language}`}</p>
        <h1 className="hero__title">{title.title_name}</h1>
        <p className="hero__meta">
          {title.genre_primary} · {title.release_year}
          {isPremiumLocked && <span className="hero__badge">Premium</span>}
        </p>
        <p className="hero__synopsis">{title.synopsis}</p>
        <div className="hero__actions">
          <button className="hero__cta hero__cta--primary" onClick={() => onSelect(item)}>
            ▶ Play
          </button>
          <button className="hero__cta hero__cta--secondary" onClick={() => onSelect(item)}>
            More Info
          </button>
        </div>
      </div>

      <div className="hero__dots">
        {items.map((it, i) => (
          <button
            key={it.title.title_id}
            className={`hero__dot ${i === index ? 'hero__dot--active' : ''}`}
            aria-label={`Go to slide ${i + 1}`}
            onClick={() => goTo(i)}
          >
            {i === index && (
              <span
                className="hero__dot-fill"
                key={`${title.title_id}-${paused}`}
                style={{ animationDuration: `${AUTOPLAY_MS}ms`, animationPlayState: paused ? 'paused' : 'running' }}
              />
            )}
          </button>
        ))}
      </div>
    </section>
  );
}
